import { calculateFileHash } from '@/utils/crypto'
import { apiRequest } from '@rh/shared/lib/api-client'

/**
 * Upload configuration from server
 */
interface UploadConfig {
  upload: {
    maxResumeFileSizeMB: number
    maxResumeFileSizeBytes: number
    maxAssetFileSizeMB: number
    maxAssetFileSizeBytes: number
    apiGatewayLimitMB: number
    apiGatewayLimitBytes: number
  }
}

let cachedConfig: UploadConfig | null = null

/**
 * Get upload configuration from server
 */
async function getUploadConfig(): Promise<UploadConfig> {
  if (cachedConfig) {
    return cachedConfig
  }

  try {
    cachedConfig = await apiRequest<UploadConfig>('/config', {
      method: 'GET',
    })
    return cachedConfig
  } catch (error) {
    console.error('Failed to fetch upload config, using defaults:', error)
    // Return defaults if fetch fails
    return {
      upload: {
        maxResumeFileSizeMB: 50,
        maxResumeFileSizeBytes: 50 * 1024 * 1024,
        maxAssetFileSizeMB: 10,
        maxAssetFileSizeBytes: 10 * 1024 * 1024,
        apiGatewayLimitMB: 10,
        apiGatewayLimitBytes: 10 * 1024 * 1024,
      },
    }
  }
}

/**
 * Check if a resume exists on the server
 */
async function checkExists(
  hash: string
): Promise<{ exists: boolean; hash: string; key?: string }> {
  const response = await apiRequest<{
    exists: boolean
    hash: string
    key?: string
  }>(`/resumes/check/${hash}`, {
    method: 'GET',
  })
  return response
}

/**
 * Upload file directly through API (proxied to S3)
 * Only works for files <= 10MB due to API Gateway limits
 */
async function uploadDirect(
  hash: string,
  file: File
): Promise<{ success: boolean; hash: string; key: string }> {
  const response = await apiRequest<{
    success: boolean
    hash: string
    key: string
  }>(`/resumes/upload/${hash}`, {
    method: 'POST',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  })
  return response
}

/**
 * Upload file using presigned S3 URL (for files > 10MB)
 * This bypasses API Gateway and uploads directly to S3
 */
async function uploadViaPresignedUrl(
  hash: string,
  file: File
): Promise<{ success: boolean; hash: string; key: string }> {
  // Step 1: Get presigned URL from API
  const urlResponse = await apiRequest<{
    uploadUrl: string
    hash: string
    key: string
    expiresIn: number
  }>('/resumes/upload-url', {
    method: 'POST',
    json: {
      hash,
      contentType: file.type || 'application/pdf',
      fileSize: file.size,
    },
  })

  // Step 2: Upload directly to S3 using presigned URL
  const uploadResponse = await fetch(urlResponse.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/pdf',
      'Content-Length': file.size.toString(),
    },
  })

  if (!uploadResponse.ok) {
    throw new Error(
      `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
    )
  }

  return {
    success: true,
    hash: urlResponse.hash,
    key: urlResponse.key,
  }
}

/**
 * Detect if running in local development mode
 * In local mode, we use LocalStack which has CORS issues with direct S3 uploads
 */
function isLocalMode(): boolean {
  // Check if we're running against localhost API
  const apiUrl = import.meta.env?.VITE_API_URL || ''
  return apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')
}

function getApiBaseUrl(): string {
  const configured = import.meta.env?.VITE_API_URL
  if (configured && configured.trim().length > 0) {
    return configured.trim()
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }

  return 'http://localhost:4000'
}

function extractFilenameFromContentDisposition(
  header: string | null
): string | null {
  if (!header) {
    return null
  }

  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  if (match?.[1]) {
    const value = match[1]
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  return null
}

async function downloadByHash(hash: string): Promise<File> {
  const apiBaseUrl = getApiBaseUrl()
  const response = await fetch(
    `${apiBaseUrl}/resumes/download-by-hash/${encodeURIComponent(hash)}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )

  if (!response.ok) {
    throw new Error(
      `Failed to download resume: ${response.status} ${response.statusText}`
    )
  }

  const contentType =
    response.headers.get('content-type') || 'application/octet-stream'
  const fallbackFilename = (() => {
    const subtype = contentType.split('/')[1]?.split(';')[0]?.split('+')[0]
    return subtype ? `${hash}.${subtype}` : `${hash}.bin`
  })()
  const filename =
    extractFilenameFromContentDisposition(
      response.headers.get('content-disposition')
    ) || fallbackFilename

  const blob = await response.blob()

  return new File([blob], filename, {
    type: contentType,
    lastModified: Date.now(),
  })
}

/**
 * Download resume as PDF by hash (converts non-PDF uploads on demand).
 */
async function downloadPdfByHash(hash: string): Promise<File> {
  const apiBaseUrl = getApiBaseUrl()
  const response = await fetch(
    `${apiBaseUrl}/resumes/pdf/${encodeURIComponent(hash)}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )

  if (!response.ok) {
    throw new Error(
      `Failed to download resume PDF: ${response.status} ${response.statusText}`
    )
  }

  const contentType = response.headers.get('content-type') || 'application/pdf'
  const filename =
    extractFilenameFromContentDisposition(
      response.headers.get('content-disposition')
    ) || `${hash}.pdf`

  const blob = await response.blob()

  return new File([blob], filename, {
    type: contentType,
    lastModified: Date.now(),
  })
}

/**
 * Complete resume upload flow:
 * 1. Get upload configuration from server
 * 2. Validate file size against max limit
 * 3. Calculate file hash
 * 4. Check if file exists
 * 5. If not, upload using appropriate method based on file size and environment
 *    - Local mode: Always use direct API upload (LocalStack has CORS issues)
 *    - Production/Dev deployed:
 *      - Files <= 10MB: Direct API upload
 *      - Files > 10MB: Presigned URL upload (bypasses API Gateway)
 * 6. Return hash
 */
async function upload(file: File): Promise<string> {
  try {
    // Stage 1: Get upload configuration
    const config = await getUploadConfig()
    const maxBytes = config.upload.maxResumeFileSizeBytes
    const maxMB = config.upload.maxResumeFileSizeMB

    // Stage 2: Validate file size
    if (file.size > maxBytes) {
      throw new Error(
        `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of ${maxMB}MB`
      )
    }

    // Stage 3: Calculate hash
    const hash = await calculateFileHash(file)

    // Stage 4: Check existence
    const existsResult = await checkExists(hash)

    if (existsResult.exists) {
      // File already exists, no need to upload
      return hash
    }

    // Stage 5: Upload using appropriate method based on file size and environment
    const isLocal = isLocalMode()

    if (isLocal) {
      // Local dev: keep direct upload (LocalStack CORS), but warn if likely to fail
      await uploadDirect(hash, file)
    } else {
      // Deployed env: always use presigned URL to bypass API Gateway limits
      await uploadViaPresignedUrl(hash, file)
    }

    return hash
  } catch (error) {
    console.error('Resume upload failed:', error)
    throw error
  }
}

export const ResumeUploadService = {
  checkExists,
  uploadDirect,
  uploadViaPresignedUrl,
  downloadByHash,
  downloadPdfByHash,
  upload,
  getUploadConfig,
}
